import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { useSite } from "@/lib/site";
import { api } from "@/api";

export default function SiteHeader() {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setProductsOpen(false); }, [location.pathname]);

  const nav = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/products", label: "Products", hasMega: true },
    { to: "/industries", label: "Industries" },
    { to: "/case-studies", label: "Case Studies" },
    { to: "/blog", label: "Knowledge" },
    { to: "/contact", label: "Contact" },
  ];

  const contactPhone = settings?.contact_phone;
  const contactEmail = settings?.contact_email;

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden lg:block bg-[#0A0A0A] text-white/70 text-xs border-b border-[#222]" data-testid="top-bar">
        <div className="container-x flex items-center justify-between h-9">
          <div className="flex items-center gap-6 font-body">
            {contactPhone && (
              <a href={`tel:${contactPhone}`} className="flex items-center gap-2 hover:text-white">
                <Phone size={12} className="text-[#C1121F]" /> {contactPhone}
              </a>
            )}
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-white">
                <Mail size={12} className="text-[#C1121F]" /> {contactEmail}
              </a>
            )}
          </div>
          <div className="flex items-center gap-4 font-body uppercase tracking-wider">
            <span>ISO 9001:2015 · IATF 16949</span>
            <span className="text-[#C1121F]">|</span>
            <span>Made in India · Shipped Worldwide</span>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#1C1C1C]/95" : "bg-[#1C1C1C]"} backdrop-blur-xl border-b border-[#333]`}
        data-testid="site-header"
      >
        <div className="container-x flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt={settings?.site_name} className="h-10 w-auto" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C1121F] flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-lg">D</span>
                </div>
                <div className="hidden sm:block">
                  <div className="font-heading font-bold text-white text-base leading-none">DURGA</div>
                  <div className="font-heading text-[10px] tracking-[0.22em] text-[#C1121F] uppercase mt-1">Auto Industries</div>
                </div>
              </div>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
            {nav.map((n) =>
              n.hasMega ? (
                <div
                  key={n.to}
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <NavLink to={n.to} className={({ isActive }) =>
                    `nav-link inline-flex items-center gap-1 ${isActive ? "text-white" : ""}`}
                    data-testid={`nav-${n.label.toLowerCase()}`}>
                    {n.label} <ChevronDown size={14} />
                  </NavLink>
                  {productsOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[640px]">
                      <div className="bg-[#1C1C1C] border border-[#333] shadow-2xl p-6 grid grid-cols-2 gap-4">
                        {categories.map((c) => (
                          <Link key={c.id} to={`/products/category/${c.slug}`}
                            className="group flex items-start gap-3 p-3 hover:bg-[#0A0A0A] border border-transparent hover:border-[#C1121F]/40 transition-all"
                            data-testid={`mega-cat-${c.slug}`}>
                            <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center text-[#C1121F] font-heading font-bold border border-[#333]">
                              {c.name?.charAt(0)}
                            </div>
                            <div>
                              <div className="font-heading font-semibold text-white text-sm">{c.name}</div>
                              <div className="font-body text-xs text-white/50 line-clamp-2">{c.description}</div>
                            </div>
                          </Link>
                        ))}
                        <Link to="/products" className="col-span-2 text-center text-xs uppercase tracking-[0.2em] text-[#C1121F] font-heading font-bold pt-2 border-t border-[#333]">
                          View All Products →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={n.to} to={n.to} className={({ isActive }) =>
                  `nav-link ${isActive ? "text-white" : ""}`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden lg:inline-flex btn-primary" data-testid="header-cta-quote">
              Request Quote
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-[#333]" data-testid="mobile-nav">
            <div className="container-x py-6 flex flex-col gap-4">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to}
                  className={({ isActive }) => `font-heading font-medium py-2 ${isActive ? "text-[#C1121F]" : "text-white/80"}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-primary mt-4">Request Quote</Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
